import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SkillsTracking — reusable component for both admin and parent portal.
 *
 * Props:
 *  - mode: "admin" | "parent"
 *  - playerId (admin) — required in admin
 *  - token (parent) — required in parent
 *  - authHeaders (admin)
 */
export default function SkillsTracking({ mode, playerId, token, authHeaders = {} }) {
  const { t, lang } = useLang();
  const es = lang === "es";

  const summaryUrl =
    mode === "admin"
      ? `${API}/admin/players/${playerId}/skills-summary`
      : `${API}/p/${token}/skills-summary`;
  const evalsUrl =
    mode === "admin"
      ? `${API}/admin/players/${playerId}/skill-evaluations`
      : `${API}/p/${token}/skill-evaluations`;

  const [summary, setSummary] = useState(null);
  const [evals, setEvals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null); // eval doc if editing

  const load = async () => {
    setLoading(true);
    const [s, e] = await Promise.all([
      axios.get(summaryUrl, { headers: authHeaders }),
      axios.get(evalsUrl, { headers: authHeaders }),
    ]);
    setSummary(s.data);
    setEvals(e.data);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [playerId, token]);

  const catalog = summary?.catalog || [];
  const latest = summary?.latest || {};
  const delta = summary?.delta || {};
  const history = summary?.history || {};

  const onSaved = async () => {
    setShowEditor(false);
    setEditing(null);
    await load();
  };

  const startEdit = (ev) => {
    setEditing(ev);
    setShowEditor(true);
  };

  const remove = async (ev) => {
    if (!window.confirm(es ? "¿Eliminar esta evaluación?" : "Delete this evaluation?")) return;
    await axios.delete(`${API}/admin/skill-evaluations/${ev.id}`, { headers: authHeaders });
    await load();
  };

  if (loading || !summary) {
    return <div className="text-neutral-500 p-10 text-center">…</div>;
  }

  const totalEvals = summary.count || 0;

  return (
    <div className="space-y-5" data-testid="skills-tracking">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-bebas text-3xl tracking-widest text-white">
            📊 {t("skills_tracking")}
          </h2>
          <p className="text-xs text-neutral-400 mt-1">
            {es
              ? "Evaluaciones 1-10 · el historial completo se guarda para ver el progreso."
              : "1-10 evaluations · full history saved to show progress."}
          </p>
        </div>
        {mode === "admin" && (
          <button
            onClick={() => {
              setEditing(null);
              setShowEditor(true);
            }}
            data-testid="skills-new-eval-btn"
            className="px-4 py-2 rounded-lg bg-[#e8559a] hover:bg-[#c9407f] text-white font-bebas tracking-widest text-sm transition-colors"
          >
            ＋ {t("skills_new_eval")}
          </button>
        )}
      </div>

      {/* Editor */}
      {showEditor && mode === "admin" && (
        <SkillEditor
          initial={editing}
          catalog={catalog}
          authHeaders={authHeaders}
          playerId={playerId}
          onCancel={() => {
            setShowEditor(false);
            setEditing(null);
          }}
          onSaved={onSaved}
          t={t}
          es={es}
        />
      )}

      {totalEvals === 0 ? (
        <div className="rounded-2xl bg-[#161616] border border-[#252525] p-10 text-center">
          <div className="text-5xl mb-3">📊</div>
          <div className="text-neutral-400">
            {es ? "Aún no hay evaluaciones registradas." : "No evaluations recorded yet."}
          </div>
          {mode === "admin" && (
            <div className="text-xs text-neutral-600 mt-1">
              {es ? "Registra la primera evaluación para iniciar el gráfico de progreso." : "Log the first evaluation to start the progress chart."}
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Overall summary card */}
          <OverallCard latest={latest} totalEvals={totalEvals} lastDate={evals[0]?.evaluation_date} t={t} es={es} />

          {/* Skill cards grid with sparkline */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {catalog.map((sk) => (
              <SkillCard
                key={sk.key}
                skill={sk}
                latest={latest[sk.key]}
                delta={delta[sk.key] || 0}
                history={history[sk.key] || []}
                es={es}
              />
            ))}
          </div>

          {/* Evaluations history list */}
          <div>
            <h3 className="font-bebas text-2xl tracking-widest text-white mb-3">
              📜 {t("skills_history")}
            </h3>
            <div className="space-y-3">
              {evals.map((ev) => (
                <EvaluationRow
                  key={ev.id}
                  ev={ev}
                  catalog={catalog}
                  mode={mode}
                  onEdit={() => startEdit(ev)}
                  onDelete={() => remove(ev)}
                  es={es}
                  t={t}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============ Overall summary card ============
function OverallCard({ latest, totalEvals, lastDate, t, es }) {
  const values = Object.values(latest);
  const avg = values.length ? Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10 : 0;
  return (
    <div className="rounded-2xl bg-gradient-to-br from-[#161616] to-[#1a0e16] border border-[#e8559a]/30 p-5 sm:p-6">
      <div className="grid grid-cols-3 gap-4 items-center">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#e8559a]/80">
            {t("skills_overall")}
          </div>
          <div className="font-bebas text-5xl sm:text-6xl text-[#e8559a] leading-none mt-1">
            {avg}<span className="text-2xl text-neutral-500">/10</span>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            {t("skills_evaluations_count")}
          </div>
          <div className="font-bebas text-3xl text-white leading-none mt-1">
            {totalEvals}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            {t("skills_last_eval")}
          </div>
          <div className="text-white text-sm mt-1">{lastDate || "—"}</div>
        </div>
      </div>
    </div>
  );
}

// ============ Skill card with sparkline ============
function SkillCard({ skill, latest, delta, history, es }) {
  const label = es ? skill.es : skill.en;
  const has = typeof latest === "number";
  const positive = delta > 0;
  const negative = delta < 0;

  return (
    <div
      data-testid={`skill-card-${skill.key}`}
      className="rounded-xl bg-[#161616] border border-[#252525] p-3.5 hover:border-[#e8559a]/40 transition-colors"
    >
      <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 mb-1">
        {label}
      </div>
      <div className="flex items-baseline gap-2">
        <div className="font-bebas text-4xl text-[#e8559a] tracking-widest leading-none">
          {has ? latest : "—"}
        </div>
        <div className="text-xs text-neutral-500">/ 10</div>
        {has && delta !== 0 && (
          <div
            className={`text-[10px] font-medium ml-auto px-1.5 py-0.5 rounded ${
              positive
                ? "text-emerald-400 bg-emerald-500/10 border border-emerald-500/30"
                : negative
                ? "text-[#e05555] bg-[#e05555]/10 border border-[#e05555]/30"
                : "text-neutral-400 border border-[#252525]"
            }`}
          >
            {positive ? "▲" : "▼"} {Math.abs(delta)}
          </div>
        )}
      </div>
      <Sparkline points={history} />
    </div>
  );
}

// ============ Sparkline (pure SVG) ============
function Sparkline({ points }) {
  if (!points || points.length === 0) {
    return <div className="mt-2 h-10 rounded bg-[#0f0f0f] border border-[#252525]" />;
  }
  const W = 200;
  const H = 40;
  const pad = 3;
  const n = points.length;
  const xStep = n > 1 ? (W - pad * 2) / (n - 1) : 0;
  const yFor = (v) => H - pad - ((v || 0) / 10) * (H - pad * 2);
  const coords = points.map((p, i) => [pad + i * xStep, yFor(p.value)]);
  const path = coords
    .map((c, i) => `${i === 0 ? "M" : "L"}${c[0].toFixed(1)},${c[1].toFixed(1)}`)
    .join(" ");
  const areaPath =
    path + ` L ${W - pad},${H - pad} L ${pad},${H - pad} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full mt-2 h-10">
      <defs>
        <linearGradient id="sparkFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#e8559a" stopOpacity="0.25" />
          <stop offset="1" stopColor="#e8559a" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#sparkFill)" />
      <path
        d={path}
        fill="none"
        stroke="#e8559a"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {coords.map((c, i) => (
        <circle key={i} cx={c[0]} cy={c[1]} r={i === coords.length - 1 ? 2.2 : 1.5} fill="#e8559a" />
      ))}
    </svg>
  );
}

// ============ Evaluation history row ============
function EvaluationRow({ ev, catalog, mode, onEdit, onDelete, es, t }) {
  const ratings = ev.ratings || {};
  return (
    <div
      data-testid={`skill-eval-${ev.id}`}
      className="rounded-xl bg-[#161616] border border-[#252525] p-4"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#e8559a]/80">
            {t("evaluation_date")}
          </div>
          <div className="font-bebas text-xl text-white tracking-widest">
            {ev.evaluation_date}
          </div>
          {ev.notes && <div className="text-xs text-neutral-400 mt-1">{ev.notes}</div>}
        </div>
        {mode === "admin" && (
          <div className="flex gap-2">
            <button
              onClick={onEdit}
              data-testid={`skill-eval-edit-${ev.id}`}
              className="px-3 py-1 rounded-md bg-[#0f0f0f] border border-[#252525] hover:border-[#e8559a]/50 text-xs text-neutral-200 transition-colors"
            >
              ✎ {t("edit")}
            </button>
            <button
              onClick={onDelete}
              data-testid={`skill-eval-del-${ev.id}`}
              className="px-3 py-1 rounded-md bg-[#e05555]/10 border border-[#e05555]/40 hover:bg-[#e05555]/20 text-xs text-[#e05555] transition-colors"
            >
              🗑
            </button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {catalog.map((sk) => {
          const v = ratings[sk.key];
          if (typeof v !== "number") return null;
          return (
            <div
              key={sk.key}
              className="rounded-md bg-[#0f0f0f] border border-[#252525] px-2.5 py-1.5 flex items-center justify-between text-xs"
            >
              <span className="text-neutral-400 truncate mr-2">
                {es ? sk.es : sk.en}
              </span>
              <span className="font-bebas tracking-widest text-[#e8559a]">
                {v}<span className="text-neutral-600">/10</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============ Editor (admin) ============
function SkillEditor({ initial, catalog, authHeaders, playerId, onCancel, onSaved, t, es }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(initial?.evaluation_date || today);
  const [notes, setNotes] = useState(initial?.notes || "");
  const [ratings, setRatings] = useState(() => {
    const r = { ...(initial?.ratings || {}) };
    // Ensure all keys present with default 5
    catalog.forEach((sk) => {
      if (typeof r[sk.key] !== "number") r[sk.key] = 5;
    });
    return r;
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) =>
    setRatings((prev) => ({ ...prev, [k]: Math.max(0, Math.min(10, parseInt(v, 10) || 0)) }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const body = { evaluation_date: date, notes, ratings };
      if (initial) {
        await axios.patch(`${API}/admin/skill-evaluations/${initial.id}`, body, {
          headers: authHeaders,
        });
      } else {
        await axios.post(`${API}/admin/players/${playerId}/skill-evaluations`, body, {
          headers: authHeaders,
        });
      }
      onSaved();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="skill-editor"
      className="rounded-2xl bg-[#161616] border border-[#e8559a]/40 p-5 sm:p-6 space-y-4"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-bebas text-2xl tracking-widest text-[#e8559a]">
          {initial ? `✎ ${t("skills_edit_eval")}` : `＋ ${t("skills_new_eval")}`}
        </h3>
        <button
          type="button"
          onClick={onCancel}
          className="text-neutral-400 hover:text-white text-xl leading-none"
        >
          ✕
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-1">
          <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
            {t("evaluation_date")} *
          </label>
          <input
            required
            type="date"
            data-testid="skill-editor-date"
            className="w-full bg-[#0f0f0f] border border-[#252525] rounded-lg px-3 py-2 text-white text-sm focus:border-[#e8559a] focus:outline-none"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
            {t("skills_notes")}
          </label>
          <input
            data-testid="skill-editor-notes"
            className="w-full bg-[#0f0f0f] border border-[#252525] rounded-lg px-3 py-2 text-white text-sm focus:border-[#e8559a] focus:outline-none"
            placeholder={es ? "Ej: Después de 4 semanas de trabajo con pie izquierdo" : "Ex: After 4 weeks of left-foot work"}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {catalog.map((sk) => (
          <SkillSlider
            key={sk.key}
            label={es ? sk.es : sk.en}
            value={ratings[sk.key] ?? 5}
            onChange={(v) => set(sk.key, v)}
            testid={`skill-slider-${sk.key}`}
          />
        ))}
      </div>

      {err && (
        <div className="rounded-lg bg-[#e05555]/10 border border-[#e05555]/40 text-[#e05555] text-sm p-2.5">
          {err}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg bg-[#0f0f0f] border border-[#252525] hover:border-neutral-500 text-neutral-300 text-sm transition-colors"
        >
          {t("cancel")}
        </button>
        <button
          type="submit"
          disabled={busy}
          data-testid="skill-editor-save"
          className="px-5 py-2 rounded-lg bg-[#e8559a] hover:bg-[#c9407f] disabled:opacity-60 text-white font-bebas tracking-widest text-sm transition-colors"
        >
          💾 {busy ? "..." : t("save")}
        </button>
      </div>
    </form>
  );
}

function SkillSlider({ label, value, onChange, testid }) {
  return (
    <div className="rounded-lg bg-[#0f0f0f] border border-[#252525] p-3">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs uppercase tracking-[0.15em] text-neutral-300">
          {label}
        </span>
        <span className="font-bebas text-2xl text-[#e8559a] tracking-widest leading-none">
          {value}<span className="text-neutral-600 text-sm">/10</span>
        </span>
      </div>
      <input
        type="range"
        min={0}
        max={10}
        step={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full accent-[#e8559a] cursor-pointer"
      />
    </div>
  );
}

export function useSkillsTranslations() {
  // No-op placeholder if we wanted to gate keys; kept for future extension.
  return null;
}
